import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu
 * @property {HTMLElement[]} [submenu]
 */

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {{ activeItem: HTMLElement | null }}
   */
  #state = {
    activeItem: null,
  };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('click', this.#documentClickListener);
    document.addEventListener('keydown', this.#documentKeydownListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('click', this.#documentClickListener);
    document.removeEventListener('keydown', this.#documentKeydownListener);
    this.#cleanupMutationObserver();
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #documentClickListener = (event) => {
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    this.#deactivate();
  };

  #documentKeydownListener = (event) => {
    if (event.key === 'Escape') {
      this.#deactivate();
    }
  };

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const trigger = findMenuItem(event.target);
    if (!trigger) return;

    const listItem = trigger.closest('.menu-list__list-item');
    if (!(listItem instanceof HTMLElement)) return;

    const controls = trigger.getAttribute('aria-controls');
    const submenu = findSubmenuFromListItem(listItem);
    const linkTrigger = findLinkTrigger(listItem);
    const buttonTrigger = findButtonTrigger(listItem);
    const primaryTrigger = buttonTrigger || linkTrigger;

    if (!primaryTrigger || !submenu) {
      this.#deactivate();
      return;
    }

    if (this.#state.activeItem === primaryTrigger && primaryTrigger.ariaExpanded === 'true') {
      this.#deactivate(primaryTrigger);
      return;
    }

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      this.#collapseItem(previouslyActiveItem);
    }

    this.#state.activeItem = primaryTrigger;
    this.ariaExpanded = 'true';
    this.dataset.overflowExpanded = 'false';

    this.#expandItem(listItem, controls);
    submenu.dataset.active = '';

    this.#cleanupMutationObserver();

    this.#submenuMutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (submenu.offsetHeight > 0) {
            this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
            this.#setFullOpenHeaderHeight(submenu.offsetHeight);
            this.#cleanupMutationObserver();
          }
        });
      });
    });

    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

    setTimeout(() => {
      this.#cleanupMutationObserver();
    }, 500);

    const finalHeight = submenu.offsetHeight || 0;

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');

    if (event.type === 'click' && trigger.matches('a[href="#"]')) {
      event.preventDefault();
    }
  };

  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const listItem = event.target.closest('.menu-list__list-item');
    const submenu = listItem instanceof HTMLElement ? findSubmenuFromListItem(listItem) : null;
    const relatedTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;

    if (relatedTarget && submenu?.contains(relatedTarget)) return;
    if (relatedTarget && listItem?.contains(relatedTarget)) return;

    this.#deactivate();
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    this.#collapseItem(item);

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
  };

  #expandItem(listItem, controls) {
    const linkTrigger = findLinkTrigger(listItem);
    const buttonTrigger = findButtonTrigger(listItem);

    if (linkTrigger && controls && linkTrigger.getAttribute('aria-controls') === controls) {
      linkTrigger.setAttribute('aria-expanded', 'true');
    }

    if (buttonTrigger && controls && buttonTrigger.getAttribute('aria-controls') === controls) {
      buttonTrigger.setAttribute('aria-expanded', 'true');
    }

    if (linkTrigger && !controls && linkTrigger.hasAttribute('aria-controls')) {
      linkTrigger.setAttribute('aria-expanded', 'true');
    }

    if (buttonTrigger && !controls && buttonTrigger.hasAttribute('aria-controls')) {
      buttonTrigger.setAttribute('aria-expanded', 'true');
    }
  }

  #collapseItem(item) {
    const listItem = item.closest('.menu-list__list-item');
    if (!(listItem instanceof HTMLElement)) return;

    const submenu = findSubmenuFromListItem(listItem);
    const linkTrigger = findLinkTrigger(listItem);
    const buttonTrigger = findButtonTrigger(listItem);

    if (linkTrigger?.hasAttribute('aria-expanded')) {
      linkTrigger.setAttribute('aria-expanded', 'false');
    }

    if (buttonTrigger?.hasAttribute('aria-expanded')) {
      buttonTrigger.setAttribute('aria-expanded', 'false');
    }

    if (submenu) {
      delete submenu.dataset.active;
    }
  }

  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const directMenuItem = element.closest('[ref="menuitem"]');
  if (directMenuItem instanceof HTMLElement) return directMenuItem;

  return null;
}

/**
 * @param {HTMLElement} listItem
 * @returns {HTMLElement | null}
 */
function findSubmenuFromListItem(listItem) {
  const submenu = listItem.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * @param {HTMLElement} listItem
 * @returns {HTMLElement | null}
 */
function findLinkTrigger(listItem) {
  const link = listItem.querySelector('.menu-list__link[aria-controls]');
  return link instanceof HTMLElement ? link : null;
}

/**
 * @param {HTMLElement} listItem
 * @returns {HTMLElement | null}
 */
function findButtonTrigger(listItem) {
  const button = listItem.querySelector('.menu-list__toggle[aria-controls]');
  return button instanceof HTMLElement ? button : null;
}
